import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/*
 * RIVIÈRE cart drawer extras:
 *  - gift wrap checkbox (adds/removes the gift wrap product)
 *  - "Ολοκλήρωσε το look" recommendations with one-click add
 * Cart mutations go through /cart/update.js and are announced with a
 * CartLinesUpdateEvent so Horizon re-renders the drawer and the cart bubble.
 */

const RECS_SECTION = 'riviere-cart-recs';
const RECS_LIMIT = 10;
const RECS_SHOW = 6;

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * @param {Element} source
 * @param {Record<string, number>} updates variantId -> quantity
 * @param {'add' | 'remove' | 'update'} action
 */
async function updateCart(source, updates, action) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: Object.entries(updates).map(([id, quantity]) => ({ merchandiseId: id, quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const cart = await response.json();
    if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: cart.sections,
        source: 'riviere-cart',
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* ---------- Gift wrap ---------- */

document.addEventListener('change', async (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('rv-wrap__input')) return;

  const label = input.closest('.rv-wrap');
  const variantId = input.dataset.variantId;
  if (!variantId) return;

  label?.classList.add('is-loading');
  try {
    await updateCart(input, { [variantId]: input.checked ? 1 : 0 }, input.checked ? 'add' : 'remove');
  } catch (error) {
    console.warn('[riviere-cart] gift wrap update failed', error);
    input.checked = !input.checked;
  } finally {
    label?.classList.remove('is-loading');
  }
});

/* ---------- Recommendations ---------- */

let lastRecsKey = '';
let recsRequest = 0;

function getRecsContainer() {
  return document.querySelector('#cart-drawer .rv-recs');
}

async function fetchRecs(url, productId, intent) {
  const params = new URLSearchParams({
    product_id: productId,
    limit: String(RECS_LIMIT),
    section_id: RECS_SECTION,
    intent,
  });
  const response = await fetch(`${url}?${params}`);
  if (!response.ok) return null;
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('.rv-recs__inner');
}

async function loadRecs(force = false) {
  const container = getRecsContainer();
  if (!(container instanceof HTMLElement)) return;

  const cartIds = (container.dataset.cartProducts || '').split(',').filter(Boolean);
  if (cartIds.length === 0) {
    container.hidden = true;
    return;
  }

  const key = cartIds.join(',');
  if (!force && key === lastRecsKey && container.childElementCount > 0) return;
  lastRecsKey = key;
  const requestId = ++recsRequest;

  const url = container.dataset.url || '/recommendations/products';
  const inCart = new Set(cartIds);
  // Most recently added item comes first in the cart.
  const sourceId = cartIds[0];

  /** @type {HTMLElement[]} */
  let cards = [];
  try {
    for (const intent of ['complementary', 'related']) {
      const inner = await fetchRecs(url, sourceId, intent);
      if (!inner) continue;
      const found = Array.from(inner.querySelectorAll('.rv-rec')).filter(
        (card) => card instanceof HTMLElement && !inCart.has(card.dataset.productId || '')
      );
      for (const card of found) {
        if (!cards.some((c) => c.dataset.productId === card.dataset.productId)) cards.push(/** @type {HTMLElement} */ (card));
      }
      if (cards.length >= 3) break;
    }
  } catch (error) {
    console.warn('[riviere-cart] recommendations failed', error);
  }

  if (requestId !== recsRequest) return;
  const live = getRecsContainer();
  if (!(live instanceof HTMLElement)) return;

  cards = cards.slice(0, RECS_SHOW);
  if (cards.length === 0) {
    live.replaceChildren();
    live.hidden = true;
    return;
  }

  const heading = document.createElement('p');
  heading.className = 'rv-recs__heading';
  heading.textContent = 'Ολοκλήρωσε το look';
  const list = document.createElement('div');
  list.className = 'rv-recs__list';
  list.append(...cards);
  live.replaceChildren(heading, list);
  live.hidden = false;
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('.rv-rec__add') : null;
  if (!(button instanceof HTMLButtonElement)) return;

  const card = button.closest('.rv-rec');
  const select = card?.querySelector('.rv-rec__select');
  const variantId = select && 'value' in select ? String(select.value) : '';
  if (!variantId) return;

  const original = button.textContent;
  button.disabled = true;
  button.textContent = '...';
  try {
    await updateCart(button, { [variantId]: 1 }, 'add');
  } catch (error) {
    console.warn('[riviere-cart] add failed', error);
    button.textContent = 'Μη διαθέσιμο';
    setTimeout(() => {
      button.textContent = original;
      button.disabled = false;
    }, 2000);
    return;
  }
  button.textContent = original;
  button.disabled = false;
});

// Re-load recommendations after any cart change, once the drawer has been morphed.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;
  Promise.resolve(promise)
    .catch(() => {})
    .then(() => setTimeout(() => loadRecs(), 350));
});

// Load when the drawer opens (and once on idle if it is already open).
function watchDrawer() {
  const dialog = document.querySelector('#cart-drawer dialog');
  if (!dialog) return;
  new MutationObserver(() => {
    if (dialog.hasAttribute('open')) loadRecs();
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  if (dialog.hasAttribute('open')) loadRecs();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchDrawer);
} else {
  watchDrawer();
}
